import type { SupWeb3Balance } from "@/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import dappConfig from "@/../config/dapp.config";
import useChainContractConfig from "@/hooks/data/useChainContractConfig";
import usePermit2ApproveCallback, { type Permit2Actions } from "@/hooks/web3/usePermit2ApproveCallback";
import { checkIfRejected, classNames, depositTransferError, notifyMessage } from "@/utils";
import { SupButton, SupLoader } from "@repo/core-ui";
import { SupCheckCircle, SupX, SupXCircle } from "@repo/core-ui";
import { formatUnits } from "viem";

interface SupWeb3DepositStepApprovalProps {
  totalBigIntAmount: bigint;
  onStart: () => void;
  onSuccess: (chainId: number, permit2Approval: Permit2Actions) => void;
  onError: (chainId: number, error: Error) => void;
  selectedBalance: SupWeb3Balance;
  disabled?: boolean;
  currentStep: number;
  allSteps: number;
  chainId: number;
}

export default function Web3DepositApprovalPermit2({
  totalBigIntAmount,
  onStart,
  onSuccess,
  onError,
  selectedBalance,
  disabled = false,
  currentStep,
  allSteps,
  chainId,
}: SupWeb3DepositStepApprovalProps) {
  const superRouterContract = useChainContractConfig(selectedBalance.chainId as number);

  const totalFormatted = parseFloat(formatUnits(totalBigIntAmount, selectedBalance.decimals)).toFixed(dappConfig.decimalsBigRoundingLimit);

  const defaultButtonText = `Sign approval ${allSteps > 1 ? `${currentStep}/${allSteps}` : ""} for ${totalFormatted} ${selectedBalance.symbol}`;

  const toastId = useMemo(
    () => `${totalBigIntAmount.toString()}_${selectedBalance.address}_${selectedBalance.chainId}`,
    [totalBigIntAmount, selectedBalance.address, selectedBalance.chainId]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessed, setIsProcessed] = useState(false);

  const onStartComposed = useCallback(() => {
    setIsLoading(true);
    setIsProcessed(false);
    onStart();
  }, [onStart]);

  const onErrorComposed = useCallback(
    (chainId: number, error: Error) => {
      setIsLoading(false);
      const rejected = checkIfRejected(error);
      notifyMessage(
        `Token Permit2 approval${rejected ? "" : " error"}`,
        rejected ? `Transaction rejected` : depositTransferError(error as Error),
        rejected ? "warning" : "error",
        <SupXCircle />,
        toastId,
        rejected ? undefined : 0
      );
      onError(chainId, error);
    },
    [onError, toastId]
  );

  const onSuccessComposed = useCallback(
    (chainId: number, permit2Approval: Permit2Actions) => {
      setIsLoading(false);
      notifyMessage("Token Permit2 approval", `Token spent for the contract successfully signed with Permit2 for ${totalFormatted}`, "success", <SupCheckCircle />);
      onSuccess(chainId, permit2Approval);
    },
    [onSuccess, totalFormatted]
  );

  const buttonText = useMemo(() => (isLoading ? "Signing tx... (check the wallet)" : defaultButtonText), [defaultButtonText, isLoading]);
  const permit2Approval = usePermit2ApproveCallback(true, selectedBalance.address, currentStep, totalBigIntAmount, superRouterContract?.address, !disabled);

  const prefixIcon = useMemo(
    () => (isLoading ? <SupLoader isSmall color="primary" className="text-neutral-700" svgClassName="stroke-2" /> : !permit2Approval.sign ? <SupX /> : undefined),
    [isLoading, permit2Approval.sign]
  );

  useEffect(() => {
    if (permit2Approval.encodedSignature) {
      onSuccessComposed(chainId, permit2Approval);
    } else {
      if (permit2Approval.isError && permit2Approval.error && !isProcessed) {
        setIsProcessed(true);
        onErrorComposed(chainId, permit2Approval.error);
      }
    }
  }, [permit2Approval, totalBigIntAmount, toastId, onSuccessComposed, onErrorComposed, chainId, isProcessed]);

  useEffect(() => {
    setIsLoading(permit2Approval.isLoading || false);
  }, [permit2Approval.isLoading]);

  const callApprove = useCallback(() => {
    try {
      if (permit2Approval.signArgs && permit2Approval.sign) {
        onStartComposed();
        permit2Approval.sign(permit2Approval.signArgs);
      }
    } catch (e: any) {
      onErrorComposed(chainId, e);
    }
  }, [chainId, onErrorComposed, onStartComposed, permit2Approval]);

  return (
    <SupButton
      fluid
      prefixIcon={prefixIcon}
      disabled={!permit2Approval.sign || isLoading || disabled}
      className={classNames("w-full justify-center", (!permit2Approval.sign || isLoading) && "cursor-not-allowed")}
      color={!permit2Approval.sign && !isLoading ? "error" : "primary"}
      onClick={callApprove}
    >
      {buttonText}
    </SupButton>
  );
}
